import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent, FocusEvent } from 'react';
import { BIP32_MAX_WORD_LENGTH, BIP32_WORDLIST } from '../utils/bip32';

interface WordInputGridProps {
  value: string[];
  onChange: (words: string[]) => void;
  id?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
  wordCount?: number;
}

const DEFAULT_WORD_COUNT = 17;

/**
 * Filter wordlist by prefix (case-insensitive).
 */
function filterWordlist(prefix: string): string[] {
  if (prefix.length === 0) {
    return [];
  }
  const lowerPrefix = prefix.toLowerCase();
  return BIP32_WORDLIST.filter((word) => word.toLowerCase().startsWith(lowerPrefix));
}

/**
 * WordInputGrid component with autocomplete for BIP32 words.
 * Default: 17 words for public key encoding.
 */
export function WordInputGrid({
  value,
  onChange,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  wordCount = DEFAULT_WORD_COUNT,
}: WordInputGridProps): JSX.Element {
  const WORD_COUNT = wordCount;
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number>(-1);
  const [visibleWords, setVisibleWords] = useState<Set<number>>(new Set());
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);

  const words = value.length === WORD_COUNT ? value : Array(WORD_COUNT).fill('');

  useEffect(() => {
    if (focusedIndex === null) {
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      return;
    }
    const currentWord = words[focusedIndex] ?? '';
    const filtered = filterWordlist(currentWord);
    setSuggestions(filtered.slice(0, 10));
    setSelectedSuggestionIndex(-1);
  }, [focusedIndex, words]);

  const handleInputChange = (index: number, newValue: string): void => {
    const newWords = [...words];
    newWords[index] = newValue;
    onChange(newWords);

    const filtered = filterWordlist(newValue);
    setSuggestions(filtered.slice(0, 10));
    setSelectedSuggestionIndex(-1);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length > 0) {
        const nextIndex =
          selectedSuggestionIndex < suggestions.length - 1
            ? selectedSuggestionIndex + 1
            : 0;
        setSelectedSuggestionIndex(nextIndex);
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (suggestions.length > 0) {
        const prevIndex =
          selectedSuggestionIndex > 0
            ? selectedSuggestionIndex - 1
            : suggestions.length - 1;
        setSelectedSuggestionIndex(prevIndex);
      }
      return;
    }

    if (e.key === 'Enter' || e.key === 'Tab') {
      if (
        selectedSuggestionIndex >= 0 &&
        selectedSuggestionIndex < suggestions.length
      ) {
        e.preventDefault();
        const selectedWord = suggestions[selectedSuggestionIndex];
        if (selectedWord !== undefined) {
          handleInputChange(index, selectedWord);
          setSuggestions([]);
          setSelectedSuggestionIndex(-1);
          if (index < WORD_COUNT - 1) {
            inputRefs.current[index + 1]?.focus();
          }
        }
        return;
      }

      if (e.key === 'Enter' && suggestions.length === 1) {
        e.preventDefault();
        const singleSuggestion = suggestions[0];
        if (singleSuggestion !== undefined) {
          handleInputChange(index, singleSuggestion);
          setSuggestions([]);
          setSelectedSuggestionIndex(-1);
          if (index < WORD_COUNT - 1) {
            inputRefs.current[index + 1]?.focus();
          }
        }
        return;
      }
    }

    if (e.key === 'Escape') {
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      return;
    }

    if (e.key === 'ArrowLeft' && (e.target as HTMLInputElement).selectionStart === 0) {
      if (index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }

    if (e.key === 'ArrowRight') {
      const input = e.target as HTMLInputElement;
      if (input.selectionStart === input.value.length) {
        if (index < WORD_COUNT - 1) {
          e.preventDefault();
          inputRefs.current[index + 1]?.focus();
        }
      }
      return;
    }

    if (e.key === 'Backspace' && (e.target as HTMLInputElement).value === '') {
      if (index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }
  };

  const handleFocus = (index: number): void => {
    setFocusedIndex(index);
    const currentWord = words[index] ?? '';
    const filtered = filterWordlist(currentWord);
    setSuggestions(filtered.slice(0, 10));
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
    if (
      suggestionsRef.current !== null &&
      suggestionsRef.current.contains(e.relatedTarget as Node)
    ) {
      return;
    }
    setFocusedIndex(null);
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
  };

  const handleSuggestionClick = (index: number, word: string): void => {
    handleInputChange(index, word);
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    if (index < WORD_COUNT - 1) {
      inputRefs.current[index + 1]?.focus();
    } else {
      inputRefs.current[index]?.blur();
    }
  };

  const allVisible = visibleWords.size === WORD_COUNT;
  const toggleAllVisibility = (): void => {
    setVisibleWords(allVisible ? new Set() : new Set(Array.from({ length: WORD_COUNT }, (_, i) => i)));
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ marginBottom: '0.5rem' }}>
        <button
          type="button"
          onClick={toggleAllVisibility}
          aria-label={allVisible ? 'Masquer les mots' : 'Afficher les mots'}
          title={allVisible ? 'Masquer les mots' : 'Afficher les mots'}
        >
          {allVisible ? 'Masquer les mots' : 'Afficher les mots'}
        </button>
      </div>
      <div
        role="group"
        aria-label={ariaLabel}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '0.5rem',
          marginBottom: focusedIndex !== null && suggestions.length > 0 ? '0.5rem' : '0',
        }}
      >
        {words.map((word, index) => (
          <div key={index} style={{ position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'stretch',
                gap: '0',
                border: '1px solid var(--color-border, #ccc)',
                borderRadius: '4px',
                backgroundColor: 'var(--color-background)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem 0.5rem 0.5rem 0.75rem',
                  fontSize: '1rem',
                  fontFamily: 'monospace',
                  color: 'var(--color-text-secondary, #666)',
                  borderRight: '1px solid var(--color-border, #ccc)',
                  minWidth: '2.25rem',
                }}
              >
                {index + 1}.
              </span>
              <label
                htmlFor={`${id ?? 'word'}-${index}`}
                style={{
                  flex: 1,
                  margin: 0,
                  minWidth: 0,
                  display: 'block',
                }}
              >
                <input
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  id={`${id ?? 'word'}-${index}`}
                  type={visibleWords.has(index) ? 'text' : 'password'}
                  value={word}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleInputChange(index, e.target.value);
                  }}
                  onKeyDown={(e) => {
                    handleKeyDown(index, e);
                  }}
                  onFocus={() => {
                    handleFocus(index);
                  }}
                  onBlur={handleBlur}
                  autoComplete="off"
                  spellCheck={false}
                  name={`bip32-w-${id ?? 'w'}-${index}`}
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                  aria-describedby={
                    index === 0 && ariaDescribedBy !== undefined
                      ? ariaDescribedBy
                      : undefined
                  }
                  size={BIP32_MAX_WORD_LENGTH}
                  style={{
                    width: `${BIP32_MAX_WORD_LENGTH}ch`,
                    minWidth: `${BIP32_MAX_WORD_LENGTH}ch`,
                    maxWidth: `${BIP32_MAX_WORD_LENGTH}ch`,
                    padding: '0.5rem 0.75rem',
                    fontSize: '1rem',
                    fontFamily: 'monospace',
                    border: 'none',
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                    color: 'var(--color-text)',
                    boxSizing: 'content-box',
                  }}
                />
              </label>
            </div>
            {focusedIndex === index && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                role="listbox"
                aria-label="Suggestions"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border, #ccc)',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: '0.25rem',
                }}
              >
                {suggestions.map((suggestion, suggestionIndex) => (
                  <div
                    key={suggestion}
                    role="option"
                    aria-selected={suggestionIndex === selectedSuggestionIndex}
                    onClick={() => {
                      handleSuggestionClick(index, suggestion);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    style={{
                      padding: '0.5rem',
                      cursor: 'pointer',
                      backgroundColor:
                        suggestionIndex === selectedSuggestionIndex
                          ? 'var(--color-primary, #007bff)'
                          : 'transparent',
                      color:
                        suggestionIndex === selectedSuggestionIndex
                          ? 'var(--color-primary-text, #fff)'
                          : 'var(--color-text)',
                    }}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
